import { makeMonitorRepository } from "../repositories/repositoryBuilders.js";
import hyphenator from "../hyphenator.js";
import langEnUS from "../translations/en-US.json";
import Locale from "../Locale.js";
import numbroDaDK from "numbro/languages/da-DK.js";
import numbroDeDE from "numbro/languages/de-DE.js";
import numbroEnGB from "numbro/languages/en-GB.js";
import numbroEsES from "numbro/languages/es-ES.js";
import numbroFiFI from "numbro/languages/fi-FI.js";
import numbroNbNO from "numbro/languages/nb-NO.js";
import numbroNlNL from "numbro/languages/nl-NL.js";
import numbroSvSE from "numbro/languages/sv-SE.js";
import polyglot, { _ } from "../polyglot/polyglot.js";
import session from "../session.js";

let instance;

// a Map of locale code to Locale instance:
let locales;

/**
 * TODO: write documentation
 */
export default class InternationalizationService {
	constructor() {
		this._locales = initializeLocales();

		this._translationsRepository = makeMonitorRepository({
			route: "/Shared/Translation/#localeCode",
			antiForgeryToken: session.getAntiForgeryToken(),
		});
	}

	getLocale(localeCode) {
		return this._locales.get(localeCode);
	}

	allLocales() {
		const result = [];

		for (const locale of this._locales.values()) {
			result.push(locale);
		}

		return result;
	}

	visibleLocales() {
		return this.allLocales().filter((locale) => locale.isVisible());
	}

	findDefaultLocale() {
		return this.getLocale("en-US");
	}

	ensureTranslations(localeCode) {
		return this.hasTranslations(localeCode)
			? Promise.resolve()
			: this._fetchTranslations(localeCode);
	}

	hasTranslations(localeCode) {
		const locale = this.getLocale(localeCode);

		if (!locale) {
			throw new Error(`No such locale ${localeCode}`);
		}

		return locale.hasTranslations();
	}

	getTranslation({ localeCode, key }) {
		const locale = this.getLocale(localeCode);

		return locale && locale.getTranslation(key);
	}

	hyphenate({ localeCode, object }) {
		const locale = this.getLocale(localeCode);

		if (!locale) {
			return object;
		}

		return locale.hyphenate(object);
	}

	withoutHyphenation(callback) {
		const hyphenateFunction = hyphenator.hyphenate;

		hyphenator.hyphenate = (text) => text;

		try {
			callback();
		} finally {
			hyphenator.hyphenate = hyphenateFunction;
		}
	}

	_fetchTranslations(localeCode) {
		const locale = this.getLocale(localeCode);

		return this._translationsRepository
			.get({
				filter: { localeCode },
				errorMessage: _(
					"There was an error fetching translations for {localeName}",
					{ localeName: locale.getName() },
				),
			})
			.then((translations) => locale.setTranslations(translations));
	}

	static instance() {
		instance ||= new this();

		return instance;
	}
}

/**
 * Initialize the locales for the translation service.
 * This function should be called once when the application starts.
 *
 * @return {Map<string, Locale>} - A map of locale code (BCP 47) to Locale
 *   instance
 */
export function initializeLocales() {
	if (locales) {
		// locales have already been initialized
		return locales;
	}

	locales = _buildLocales();

	polyglot.setLocales(locales);

	return locales;
}

function _buildLocales() {
	const localeSpecs = [
		{
			// This locale is the one Crowdin uses for In-Context
			// translations. I chose it because chances are low
			// that we need to translate the app to this language
			// in the upcoming future:
			code: "aa-ER",
			name: _("Afar"),
			visible: false,
		},
		{
			code: "da-DK",
			numbroLanguage: numbroDaDK,
			flagCssClass: "danish",
			name: _("Danish"),
			hyphenation: "da",
		},
		{
			code: "de-DE",
			numbroLanguage: numbroDeDE,
			flagCssClass: "german",
			name: _("German"),
		},
		{
			code: "en-US",
			translations: langEnUS,
			flagCssClass: "english",
			name: _("English (US)"),
			hyphenation: "en",
		},
		{
			code: "en-GB",
			numbroLanguage: numbroEnGB,
			flagCssClass: "english-gb",
			name: _("English (GB)"),
			hyphenation: "en",
		},
		{
			code: "es-ES",
			numbroLanguage: numbroEsES,
			flagCssClass: "spanish",
			name: _("Spanish"),
		},
		{
			code: "fi-FI",
			numbroLanguage: numbroFiFI,
			flagCssClass: "finnish",
			name: _("Finnish"),
			hyphenation: "fi",
			visible: false,
		},
		{
			code: "nb-NO",
			numbroLanguage: numbroNbNO,
			flagCssClass: "norwegian",
			name: _("Norwegian"),
			hyphenation: "no",
		},
		{
			code: "nl-NL",
			numbroLanguage: numbroNlNL,
			flagCssClass: "dutch",
			name: _("Dutch"),
		},
		{
			code: "sv-SE",
			numbroLanguage: numbroSvSE,
			flagCssClass: "swedish",
			name: _("Swedish"),
			hyphenation: "sv",
		},
	];

	// eslint-disable-next-line no-shadow -- bad practice, we should fix this
	const locales = new Map();

	localeSpecs.forEach((spec) => {
		locales.set(spec.code, new Locale(spec));
	});

	return locales;
}
